import React from 'react';
import { Grid, Paper, Typography, Box } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MouseIcon from '@mui/icons-material/Mouse';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';

const MetricCard = ({ title, value, icon, suffix, color, gradient }) => {
  const gradientColors = gradient || ['#6366f1', '#8b5cf6'];
  
  return (
    <Paper 
      sx={{ 
        p: 3, 
        display: 'flex', 
        flexDirection: 'column', 
        height: 160,
        position: 'relative',
        overflow: 'hidden',
        background: `linear-gradient(135deg, ${gradientColors[0]}15 0%, ${gradientColors[1]}08 100%)`,
        border: `1px solid ${gradientColors[0]}20`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 10px 25px -5px ${gradientColors[0]}30`,
          borderColor: `${gradientColors[0]}40`,
        },
      }}
    >
      {/* Decorative gradient circle */}
      <Box
        sx={{
          position: 'absolute',
          top: -20,
          right: -20,
          width: 100,
          height: 100,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${gradientColors[0]}20, ${gradientColors[1]}10)`,
          opacity: 0.6,
        }}
      />
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
        <Typography 
          variant="body2" 
          sx={{ 
            color: 'text.secondary',
            fontWeight: 500,
            fontSize: '0.875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          {title}
        </Typography>
        <Box 
          sx={{ 
            color: color || gradientColors[0],
            background: `linear-gradient(135deg, ${gradientColors[0]}, ${gradientColors[1]})`,
            borderRadius: '10px',
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 12px ${gradientColors[0]}30`,
          }}
        >
          {icon}
        </Box>
      </Box>
      <Typography 
        component="p" 
        variant="h4" 
        sx={{ 
          my: 'auto',
          fontWeight: 700,
          background: `linear-gradient(135deg, ${gradientColors[0]}, ${gradientColors[1]})`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {value}
        {suffix && (
          <Typography 
            component="span" 
            variant="body2" 
            sx={{ 
              ml: 1,
              color: 'text.secondary',
              fontWeight: 500,
            }}
          >
            {suffix}
          </Typography>
        )}
      </Typography>
    </Paper>
  );
};

const MetricsOverview = ({ metrics }) => {
  // Calculate total values from metrics data
  const calculateTotals = () => {
    if (!metrics || metrics.length === 0) {
      return {
        impressions: 0,
        clicks: 0,
        spend: 0,
        conversions: 0,
        revenue: 0,
        ctr: 0,
        cpc: 0,
        costPerConversion: 0,
        roas: 0
      };
    }
    
    let totals = {
      impressions: 0,
      clicks: 0,
      spend: 0,
      conversions: 0,
      revenue: 0
    };
    
    metrics.forEach(metric => {
      totals.impressions += metric.impressions || 0;
      totals.clicks += metric.clicks || 0;
      totals.spend += metric.spend || 0;
      totals.conversions += metric.conversions || 0;
      totals.revenue += metric.revenue || 0;
    });
    
    // Calculate derived metrics
    totals.ctr = totals.impressions > 0 ? (totals.clicks / totals.impressions * 100) : 0;
    totals.cpc = totals.clicks > 0 ? (totals.spend / totals.clicks) : 0;
    totals.costPerConversion = totals.conversions > 0 ? (totals.spend / totals.conversions) : 0;
    totals.roas = totals.spend > 0 ? (totals.revenue / totals.spend) : 0;
    
    return totals;
  };
  
  const totals = calculateTotals();
  
  // Format numbers for display
  const formatNumber = (value, decimals = 0) => {
    if (value >= 1000000) {
      return (value / 1000000).toFixed(1) + 'M';
    } else if (value >= 1000) {
      return (value / 1000).toFixed(1) + 'k';
    } else {
      return value.toFixed(decimals);
    }
  };
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2
    }).format(value);
  };
  
  // Define gradient colors for each metric card
  const metricGradients = [
    ['#6366f1', '#8b5cf6'], // Impressões - Indigo to Purple
    ['#3b82f6', '#6366f1'], // Cliques - Blue to Indigo
    ['#10b981', '#3b82f6'], // CTR - Green to Blue (positive) or ['#ef4444', '#f59e0b'] (negative)
    ['#f59e0b', '#f97316'], // CPC - Amber to Orange
    ['#8b5cf6', '#a855f7'], // Investimento - Purple to Purple
    ['#10b981', '#34d399'], // Conversões - Green to Light Green
    ['#ec4899', '#f472b6'], // Custo/Conversão - Pink to Light Pink
    ['#10b981', '#3b82f6'], // ROAS - Green to Blue (positive) or ['#ef4444', '#f59e0b'] (negative)
  ];

  return (
    <Box>
      <Typography 
        variant="h5" 
        sx={{ 
          mb: 3,
          fontWeight: 700,
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        Visão Geral
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard 
            title="Impressões" 
            value={formatNumber(totals.impressions)} 
            icon={<VisibilityIcon sx={{ color: 'white' }} />} 
            gradient={metricGradients[0]}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard 
            title="Cliques" 
            value={formatNumber(totals.clicks)} 
            icon={<MouseIcon sx={{ color: 'white' }} />} 
            gradient={metricGradients[1]}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard 
            title="CTR" 
            value={totals.ctr.toFixed(2)} 
            suffix="%" 
            icon={totals.ctr >= 2 ? <TrendingUpIcon sx={{ color: 'white' }} /> : <TrendingDownIcon sx={{ color: 'white' }} />}
            gradient={totals.ctr >= 2 ? ['#10b981', '#34d399'] : ['#ef4444', '#f87171']}
            color={totals.ctr >= 2 ? '#10b981' : '#ef4444'}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard 
            title="CPC" 
            value={formatCurrency(totals.cpc)} 
            icon={<MonetizationOnIcon sx={{ color: 'white' }} />} 
            gradient={metricGradients[3]}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard 
            title="Investimento" 
            value={formatCurrency(totals.spend)} 
            icon={<MonetizationOnIcon sx={{ color: 'white' }} />} 
            gradient={metricGradients[4]}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard 
            title="Conversões" 
            value={formatNumber(totals.conversions)} 
            icon={<ShoppingCartIcon sx={{ color: 'white' }} />} 
            gradient={metricGradients[5]}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard 
            title="Custo/Conversão" 
            value={formatCurrency(totals.costPerConversion)} 
            icon={<MonetizationOnIcon sx={{ color: 'white' }} />} 
            gradient={metricGradients[6]}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard 
            title="ROAS" 
            value={totals.roas.toFixed(2)} 
            suffix="x" 
            icon={totals.roas >= 1 ? <TrendingUpIcon sx={{ color: 'white' }} /> : <TrendingDownIcon sx={{ color: 'white' }} />}
            gradient={totals.roas >= 1 ? ['#10b981', '#34d399'] : ['#ef4444', '#f87171']}
            color={totals.roas >= 1 ? '#10b981' : '#ef4444'}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default MetricsOverview;
